import React, { memo } from 'react';
import { Lock } from 'lucide-react';
import { useLanguage } from '../../contexts';

interface PremiumBadgeProps {
  /** Icon size in pixels */
  size?: number;
  /** Additional CSS classes for the badge container */
  className?: string;
}

/**
 * PremiumBadge - Lock icon overlay for premium-gated content.
 *
 * Renders a small circular badge with a lock icon, absolutely positioned
 * in the top-end corner (RTL-aware). Parent must have `position: relative`.
 *
 * @example
 * <div className="relative">
 *   <PremiumBadge />
 *   <SomeContent />
 * </div>
 */
export const PremiumBadge: React.FC<PremiumBadgeProps> = memo(({ size = 14, className = '' }) => {
  const { isRTL } = useLanguage();

  return (
    <div
      className={`
        absolute top-1 ${isRTL ? 'left-1' : 'right-1'}
        sm:top-1.5 ${isRTL ? 'sm:left-1.5' : 'sm:right-1.5'}
        bg-gray-500/90 dark:bg-gray-600/90
        text-white rounded-full p-1 sm:p-1.5
        shadow-md z-10
        ${className}
      `}
    >
      <Lock size={size} />
    </div>
  );
});

PremiumBadge.displayName = 'PremiumBadge';
